import { GeoCodeResult } from '@/hotdesk-ui/types/geo-code-result.type';
import { isRegionWideGeo } from '@/hotdesk-ui/helpers/explore-location-params';

/**
 * Recent searches for the landing search bar's empty-state panel.
 *
 * Deliberately separate from `updateRecentSearches` in `location.helper.ts`:
 * that one writes whole `GeoCodeResult` objects through `storeSet`, which is
 * cookie-backed — a few entries blow past the ~4KB cookie limit and ride along
 * on every request. This keeps a trimmed record in localStorage instead.
 */
export interface RecentSearch {
    /** Stable list key — Google place id when known, else the coordinates. */
    id: string;
    label: string;
    lat: number;
    lng: number;
    city?: string;
    country?: string;
    countryCode?: string;
    /**
     * The entry is a whole country ("Egypt"), not a point. Re-running it has to
     * search the country by name — a radius search around a country's stored
     * coordinates hits its geometric centre, which is usually empty land and
     * returns nothing.
     */
    isCountry?: boolean;
    /**
     * English name when the entry is a whole state ("California"). Same reason
     * as `isCountry`: re-running it as a point search would only cover a
     * radius around the state's centre.
     */
    regionName?: string;
}

export const RECENT_SEARCHES_KEY = 'hd_recent_searches';
export const RECENT_SEARCHES_LIMIT = 3;

/**
 * Entries stored before `isCountry` existed don't record whether they were a
 * country. A row with no city, labelled exactly like its own country, was one —
 * without this an older "Egypt" entry re-runs as a point search on the
 * country's centroid and finds nothing.
 */
const withInferredIsCountry = (entry: RecentSearch): RecentSearch => {
    if (entry.isCountry !== undefined) return entry;
    const looksCountryWide =
        !entry.city &&
        !!entry.countryCode &&
        !!entry.country &&
        entry.label.trim().toLowerCase() ===
            entry.country.trim().toLowerCase();
    return looksCountryWide ? { ...entry, isCountry: true } : entry;
};

export const readRecentSearches = (): RecentSearch[] => {
    if (typeof window === 'undefined') return [];
    try {
        const raw = window.localStorage.getItem(RECENT_SEARCHES_KEY);
        if (!raw) return [];
        const parsed = JSON.parse(raw);
        if (!Array.isArray(parsed)) return [];
        return parsed
            .filter(
                (entry): entry is RecentSearch =>
                    !!entry &&
                    typeof entry.label === 'string' &&
                    typeof entry.lat === 'number' &&
                    typeof entry.lng === 'number'
            )
            .slice(0, RECENT_SEARCHES_LIMIT)
            .map(withInferredIsCountry);
    } catch {
        return [];
    }
};

/** Prepends `entry`, drops any earlier copy of the same place, and caps. */
export const addRecentSearch = (entry: RecentSearch): RecentSearch[] => {
    if (typeof window === 'undefined') return [];
    const existing = readRecentSearches();
    const deduped = existing.filter(
        (item) =>
            item.id !== entry.id &&
            item.label.trim().toLowerCase() !== entry.label.trim().toLowerCase()
    );
    const next = [entry, ...deduped].slice(0, RECENT_SEARCHES_LIMIT);
    try {
        window.localStorage.setItem(RECENT_SEARCHES_KEY, JSON.stringify(next));
    } catch {
        // Quota or private-mode failure — the list is a convenience, not state
        // anything depends on, so a failed write is fine to swallow.
    }
    return next;
};

export const clearRecentSearches = (): void => {
    if (typeof window === 'undefined') return;
    try {
        window.localStorage.removeItem(RECENT_SEARCHES_KEY);
    } catch {
        /* ignore */
    }
};

/** Narrows a resolved place down to what the panel needs to re-run it. */
export const toRecentSearch = (
    result: GeoCodeResult
): RecentSearch | null => {
    const label = result?.formatted_address?.trim();
    const lat = result?.lat ?? result?.geometry?.location?.lat;
    const lng = result?.log ?? result?.geometry?.location?.lng;
    if (!label || typeof lat !== 'number' || typeof lng !== 'number') {
        return null;
    }
    return {
        id: result.place_id || `${lat},${lng}`,
        label,
        lat,
        lng,
        city: result.city,
        country: result.country,
        countryCode: result.country_sort,
        isCountry: result.types?.includes('country') || undefined,
        regionName: isRegionWideGeo(result) ? result.region_name : undefined
    };
};
